<script setup lang="ts">
/**
 * SWIPE DECK — one item grid, two behaviours by screen size.
 *
 *  • Desktop (>640px): a plain auto-fill grid — the page wrappers collapse via
 *    `display:contents`, so items flow exactly like a normal grid. Pass the
 *    column min-width / gap so it matches the host's look.
 *  • Mobile (≤640px): a 2-column, `pageSize`-per-page horizontal carousel with
 *    CSS scroll-snap — the player swipes left/right between pages (no page ever
 *    scrolls sideways; each page is exactly the deck's width). Dots + ‹ › below.
 *
 * The item markup is provided by the default scoped slot, so the host keeps a
 * single copy of its tile. Trailing empty cells (`padClass`) fill the desktop
 * grid up to `minCells` and top the last mobile page up to a full row.
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = withDefaults(defineProps<{
  items: readonly unknown[];
  pageSize?: number;      // items per mobile page (2 cols × N rows)
  minCol?: string;        // desktop grid column min-width, e.g. '150px'
  gap?: string;           // desktop grid gap, e.g. '0.75rem'
  minCells?: number;      // desktop: pad with empty cells up to this many
  padPage?: boolean;      // mobile: pad the last page up to a full pageSize
  padClass?: string;      // class applied to the empty pad cells
  itemKey?: (item: unknown, index: number) => string | number;
}>(), {
  pageSize: 6,
  minCol: '150px',
  gap: '0.75rem',
  minCells: 0,
  padPage: false,
  padClass: '',
});

const deck = ref<HTMLElement | null>(null);
const current = ref(0);

const isMobile = ref(false);
let mql: MediaQueryList | null = null;
function syncMobile() { isMobile.value = !!mql?.matches; }

const pages = computed(() => {
  const out: unknown[][] = [];
  for (let i = 0; i < props.items.length; i += props.pageSize) {
    out.push(props.items.slice(i, i + props.pageSize) as unknown[]);
  }
  return out;
});

// Empty cells trailing the LAST page: desktop fills to minCells, mobile tops the
// final page to a full pageSize. On desktop `display:contents` lets them flow
// into the grid as ordinary trailing cells.
const padCount = computed(() => {
  const n = props.items.length;
  if (isMobile.value) {
    if (!props.padPage || n === 0) return 0;
    return (props.pageSize - (n % props.pageSize)) % props.pageSize;
  }
  return Math.max(0, props.minCells - n);
});

function keyOf(item: unknown, index: number) {
  if (props.itemKey) return props.itemKey(item, index);
  const id = (item as { id?: string | number } | null)?.id;
  return id ?? index;
}

function onScroll() {
  if (!deck.value) return;
  const w = deck.value.clientWidth || 1;
  current.value = Math.round(deck.value.scrollLeft / w);
}
function goTo(i: number) {
  if (!deck.value) return;
  const clamped = Math.max(0, Math.min(pages.value.length - 1, i));
  deck.value.scrollTo({ left: clamped * deck.value.clientWidth, behavior: 'smooth' });
}

// Reset to the first page whenever the item set changes (e.g. filter switch).
watch(() => props.items, async () => {
  current.value = 0;
  await nextTick();
  if (deck.value) deck.value.scrollLeft = 0;
});

onMounted(() => {
  mql = window.matchMedia('(max-width: 640px)');
  syncMobile();
  mql.addEventListener?.('change', syncMobile);
});
onUnmounted(() => mql?.removeEventListener?.('change', syncMobile));
</script>

<template>
  <div class="deck-wrap">
    <div
      ref="deck" class="deck"
      :style="{ '--deck-min': minCol, '--deck-gap': gap }"
      @scroll.passive="onScroll"
    >
      <div v-for="(pg, pi) in pages" :key="pi" class="deck-page">
        <slot
          v-for="(it, ii) in pg" :key="keyOf(it, pi * pageSize + ii)"
          :item="it" :index="pi * pageSize + ii"
        />
        <template v-if="pi === pages.length - 1 && padCount > 0">
          <div v-for="n in padCount" :key="'pad-' + n" class="deck-pad" :class="padClass"></div>
        </template>
      </div>
    </div>

    <!-- mobile-only pager: ‹ 3 / 18 › with dots when the count is small -->
    <div v-if="pages.length > 1" class="deck-nav" aria-hidden="false">
      <button class="deck-arrow" :disabled="current === 0" @click="goTo(current - 1)" aria-label="Previous page">‹</button>
      <div v-if="pages.length <= 10" class="deck-dots">
        <button
          v-for="(pg, pi) in pages" :key="'dot-' + pi"
          class="deck-dot" :class="{ 'deck-dot-on': pi === current }"
          @click="goTo(pi)" :aria-label="`Page ${pi + 1}`"
        ></button>
      </div>
      <span v-else class="deck-count">{{ current + 1 }} / {{ pages.length }}</span>
      <button class="deck-arrow" :disabled="current === pages.length - 1" @click="goTo(current + 1)" aria-label="Next page">›</button>
    </div>
  </div>
</template>

<style scoped>
.deck-wrap { min-width: 0; }

/* ── desktop: page wrappers dissolve, items flow in one auto-fill grid ── */
.deck {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--deck-min, 150px), 1fr));
  gap: var(--deck-gap, 0.75rem);
}
.deck-page { display: contents; }
.deck-nav { display: none; }

/* trailing empty cells (desktop min-fill / last mobile page) */
.deck-pad {
  border: 1px dashed var(--color-border, #2a2436);
  border-radius: 9px;
  min-height: 100px;
  background: rgba(0, 0, 0, 0.12);
}

/* ── mobile: horizontal scroll-snap carousel, 2 columns per page ── */
@media (max-width: 640px) {
  .deck {
    display: flex;
    grid-template-columns: none;
    gap: 0;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .deck::-webkit-scrollbar { display: none; }
  .deck-page {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: max-content;
    gap: 10px;
    flex: 0 0 100%;
    width: 100%;
    scroll-snap-align: start;
    padding: 3px 2px;   /* keep card shadows/hover-lift from clipping */
  }

  .deck-nav {
    display: flex; align-items: center; justify-content: center; gap: 12px;
    margin-top: 12px;
  }
  .deck-arrow {
    display: grid; place-items: center;
    width: 30px; height: 30px;
    font-family: var(--font-heading, serif); font-size: 18px; line-height: 1;
    color: var(--color-gold);
    border: 1px solid color-mix(in srgb, var(--color-gold) 40%, transparent);
    background: rgba(0, 0, 0, 0.3); border-radius: 999px;
  }
  .deck-arrow:disabled { opacity: 0.3; }
  .deck-dots { display: flex; align-items: center; gap: 7px; }
  .deck-dot {
    width: 7px; height: 7px; border-radius: 999px;
    border: 0; padding: 0;
    background: color-mix(in srgb, var(--color-text-dim) 60%, transparent);
    transition: background 150ms ease, transform 150ms ease;
  }
  .deck-dot-on { background: var(--color-gold); transform: scale(1.35); }
  .deck-count {
    font-family: var(--font-heading, serif); font-size: 13px;
    color: var(--color-text-dim); font-variant-numeric: tabular-nums;
    min-width: 54px; text-align: center;
  }
}
</style>
